import { lazy, Suspense, type FC, type ReactNode } from "react";

import { AutomationsPageSkeleton } from "@/routes/automations/AutomationsPageSkeleton";

type LazyRouteLoader = () => Promise<{ default: FC }>;

const createSuspendedRoute = (loader: LazyRouteLoader, fallback: ReactNode = <AutomationsPageSkeleton />): FC => {
    const LazyRoute = lazy(loader);

    return function SuspendedRoute() {
        return (
            <Suspense fallback={fallback}>
                <LazyRoute />
            </Suspense>
        );
    };
};

const noFallback = null;

const loadAutomationsPageRoute = () =>
    import("@/routes/automations/AutomationsPage").then((m) => ({
        default: m.AutomationsPage,
    }));

const loadWebhooksRoute = () =>
    Promise.all([
        import("@/routes/automations/RequireWebhooksCapability"),
        import("@/components/webhooks/WebhooksSidePanel"),
    ]).then(([guard, panel]) => ({
        default: function WebhooksRoute() {
            return (
                <guard.RequireWebhooksCapability>
                    <panel.WebhooksSidePanel />
                </guard.RequireWebhooksCapability>
            );
        },
    }));

const loadWebhookDetailsRoute = () =>
    import("@/routes/automations/WebhookDetailsPage").then((m) => ({
        default: m.WebhookDetailsPage,
    }));

const loadNewAutomationRoute = () =>
    Promise.all([
        import("@/routes/automations/RequireFullAutomationAccess"),
        import("@/routes/automations/NewAutomationPage"),
    ]).then(([guard, page]) => ({
        default: function NewAutomationRoute() {
            return (
                <guard.RequireFullAutomationAccess>
                    <page.NewAutomationPage />
                </guard.RequireFullAutomationAccess>
            );
        },
    }));

const loadAutomationDetailsRoute = () =>
    Promise.all([
        import("@/routes/automations/RequireFullAutomationAccess"),
        import("@/routes/automations/automation-details-page/AutomationDetailsPage"),
    ]).then(([guard, page]) => ({
        default: function AutomationDetailsRoute() {
            return (
                <guard.RequireFullAutomationAccess>
                    <page.AutomationDetailsPage />
                </guard.RequireFullAutomationAccess>
            );
        },
    }));

const loadAutomationEditRoute = () =>
    Promise.all([
        import("@/routes/automations/RequireFullAutomationAccess"),
        import("@/routes/automations/automation-edit-page/AutomationEditPage"),
    ]).then(([guard, page]) => ({
        default: function AutomationEditRoute() {
            return (
                <guard.RequireFullAutomationAccess>
                    <page.AutomationEditPage />
                </guard.RequireFullAutomationAccess>
            );
        },
    }));

const loadAutomationExecutionLayoutRoute = () =>
    Promise.all([
        import("@/routes/automations/RequireFullAutomationAccess"),
        import("@/routes/automations/AutomationExecutionLayout"),
    ]).then(([guard, layout]) => ({
        default: function AutomationExecutionLayoutRoute() {
            return (
                <guard.RequireFullAutomationAccess>
                    <layout.AutomationExecutionLayout />
                </guard.RequireFullAutomationAccess>
            );
        },
    }));

const loadAutomationExecutionSummaryRoute = () =>
    import("@/routes/automations/AutomationExecutionSummaryPage").then((m) => ({
        default: m.AutomationExecutionSummaryPage,
    }));

const loadAutomationExecutionActionRoute = () =>
    import("@/routes/automations/AutomationExecutionActionPage").then((m) => ({
        default: m.AutomationExecutionActionPage,
    }));

export const AutomationsIndexRoute = createSuspendedRoute(loadAutomationsPageRoute);
export const WebhooksRoute = createSuspendedRoute(loadWebhooksRoute, noFallback);
export const WebhookDetailsRoute = createSuspendedRoute(loadWebhookDetailsRoute, noFallback);
export const NewAutomationRoute = createSuspendedRoute(loadNewAutomationRoute);
export const AutomationDetailsRoute = createSuspendedRoute(loadAutomationDetailsRoute);
export const AutomationEditRoute = createSuspendedRoute(loadAutomationEditRoute);
export const AutomationExecutionLayoutRoute = createSuspendedRoute(loadAutomationExecutionLayoutRoute);
export const AutomationExecutionSummaryRoute = createSuspendedRoute(loadAutomationExecutionSummaryRoute, noFallback);
export const AutomationExecutionActionRoute = createSuspendedRoute(loadAutomationExecutionActionRoute, noFallback);

// eslint-disable-next-line react-refresh/only-export-components
export const preloadAutomationsRouteModules = async () => {
    await Promise.all([
        loadAutomationsPageRoute(),
        loadWebhooksRoute(),
        loadWebhookDetailsRoute(),
        loadNewAutomationRoute(),
        loadAutomationDetailsRoute(),
        loadAutomationEditRoute(),
        loadAutomationExecutionLayoutRoute(),
        loadAutomationExecutionSummaryRoute(),
        loadAutomationExecutionActionRoute(),
    ]);
};
